import React, { useEffect, useState } from "react";
import PageMenu from "../components/PageMenu";
import { Card, Col, Row } from "react-bootstrap";
import BasicLineChart from "../../../../../../components/chart/BasicLineChart";
import ReactTable from "../../../../../../lib/table/react-table";
import MixedColumnChart from "../../../../../../components/chart/MixedColumnChart";
import {
  transformConsentedUniqueUsers,
  transformExposureData,
} from "../../utils/commonFunctions";
import { useParams } from "react-router-dom";
import {
  useGetProjectDetails,
  useGetSummaryConsentedBrandExposure,
  useGetSummaryConsentedHcpDetails,
  useGetSummaryConsentedUniqueUsers,
  useGetSummaryCount,
} from "../../../../../../queries/query-hooks/workspace/emsl-hook";
import dayjs from "dayjs";
import BarChartSkeleton from "../../../../../../skeleton/workspace/clirnet/brand-ads/bar-chart-skeleton";
import CommonTableLoader from "../../../../../../skeleton/common-table-loader";
import { findIcon } from "../../../../../dashboard/icons";
import useRedirect from "../../../../../../components/custom-hooks/use-redirect";
import { ProjectHeaderTopBar, TableHeader, CardNotFound } from "@ui";
import useCsvDownloader from "components/custom-hooks/use-csv-downloader";

const ConsentedLeads = () => {
  const { project_id } = useParams();
  const { redirectTo } = useRedirect();
  const { downloadCSV } = useCsvDownloader();
  const { data: projectDetails, isLoading: isProjectDetailsLoading } =
    useGetProjectDetails(project_id, true);
  const [tableOffset, setTableOffset] = useState(0);
  const [currentPageNumber, setCurrentPageNumber] = useState(0);
  const [maskedType, setMaskedType] = useState(true);

  const contentIds = projectDetails?.content
    ?.map((item) => item?.content_id)
    .join(",");

  const { data: summaryCount, isLoading: isSummaryCountLoading } =
    useGetSummaryCount(project_id, {
      start_date: projectDetails?.project_start_date,
      end_date: projectDetails?.project_end_date,
      content_id: contentIds,
    });

  const payload = {
    start_date: projectDetails?.project_start_date,
    end_date: projectDetails?.project_end_date,
    content_id: contentIds,
  };
  const tablePayload = {
    start_date: projectDetails?.project_start_date,
    end_date: projectDetails?.project_end_date,
    content_id: contentIds,
    masked_data: true,
    offset: tableOffset,
    limit: summaryCount?.consented_leads,
  };

  const { data: brandExposure, isLoading: isBrandExposureLoading } =
    useGetSummaryConsentedBrandExposure(project_id, payload);
  const { data: uniqueUsers, isLoading: isUniqueUsersLoading } =
    useGetSummaryConsentedUniqueUsers(project_id, payload);
  const { data: usersList, isLoading: isUsersListLoading } =
    useGetSummaryConsentedHcpDetails(project_id, tablePayload);

  const totalCount = usersList?.totalCount;

  const { data: unMaskedUsersList, isLoading: isUnMaskedUsersListLoading } =
    useGetSummaryConsentedHcpDetails(project_id, {
      ...tablePayload,
      offset: 0,
      limit: summaryCount?.consented_leads,
      masked_data: maskedType,
    });
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!isUnMaskedUsersListLoading && maskedType === false) {
      downloadCSV(hcpTableCsvFormat);
      setMaskedType(true);
    }
  }, [unMaskedUsersList, isUnMaskedUsersListLoading]);

  const filteredUsers = usersList?.rows?.filter(
    (user) =>
      user?.state?.toLowerCase().includes(searchText?.toLowerCase()?.trim()) ||
      user?.device?.toLowerCase().includes(searchText?.toLowerCase()?.trim()) ||
      user?.ip_address
        ?.toLowerCase()
        .includes(searchText?.toLowerCase()?.trim())
  );

  const hcpTableCsvFormat = {
    header: [
      "Name",
      "Specialty",
      "Location",
      "Device",
      "Browser",
      "IP Address",
      "Date & Time",
    ],
    column: [
      "name",
      "speciality",
      "state",
      "device",
      "browser",
      "ip_address",
      "last_interaction",
    ],
    rows: unMaskedUsersList?.rows,
  };

  const LOCATION_REPORT_TABLE_COLUMNS = [
    {
      columnId: 1,
      Header: "Name",
      accessor: "name",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return "---";
        return value.value;
      },
    },
    {
      columnId: 2,
      Header: "Specialty",
      accessor: "speciality",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return "---";
        return value.value;
      },
    },
    {
      columnId: 3,
      Header: "Location",
      accessor: "state",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return "---";
        return value.value;
      },
    },
    {
      columnId: 4,
      Header: "Device",
      accessor: "device",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return "---";
        return value.value;
      },
    },
    {
      columnId: 5,
      Header: "Browser",
      accessor: "browser",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return "---";
        return value.value;
      },
    },
    {
      columnId: 6,
      Header: "IP Address",
      accessor: "ip_address",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return "---";
        return value.value;
      },
    },
    {
      columnId: 7,
      Header: "Date & Time",
      accessor: "last_interaction",
      disableFilters: true,
      Cell: (value) => {
        if (!value.value) return <div className="text-center">---</div>;

        return (
          <div className="text-center">
            {dayjs(value.value).format("DD MMM, YYYY")}
            <br />
            {dayjs(value.value).format("hh:mm A")}
          </div>
        );
      },
    },
    {
      columnId: 8,
      Header: "Action",
      accessor: "action",
      disableFilters: true,
      disableSortBy: true,
      Cell: ({ row }) => {
        const redirectURL = `emsl/summary/consented_leads/${project_id}/leads/details/${row.original.doctor_master_id}`;
        return (
          <div className="d-flex justify-content-center align-items-center gap-3">
            <div
              role="button"
              onClick={() => redirectTo(redirectURL)}
              style={{ color: "#8A92A6" }}
            >
              {findIcon("Eye", "solid", 24)}
            </div>
          </div>
        );
      },
    },
  ];

  const formattedBrandExposureData = transformExposureData(
    brandExposure,
    "Brand Exposure",
    "#1aa053"
  );
  const formattedUniqueUsersData = transformConsentedUniqueUsers(
    uniqueUsers,
    ["Unique Users Cumulative", "Unique Users Per Day"],
    ["#1aa053", "#3256b8"]
  );

  const onTableStateChange = (e) => {
    setCurrentPageNumber(e?.pageIndex);
    setTableOffset(e?.pageIndex * 10);
  };

  const exportCSV = () => {
    setMaskedType(false);
  };

  return (
    <div className="mb-3">
      <ProjectHeaderTopBar
        projectType="eMSL"
        title="Consented Leads"
        projectStartDate={projectDetails?.project_start_date || ""}
        projectEndDate={projectDetails?.project_end_date || ""}
        status={projectDetails?.status}
        isProjectDetailsLoading={isProjectDetailsLoading}
      />
      <PageMenu name="consented_leads" />

      <Row className="mb-3 mb-md-4">
        <Col md={6} sm={12} className="d-flex">
          {!isBrandExposureLoading && brandExposure?.length > 0 ? (
            <BasicLineChart
              title={"Brand Exposure"}
              tooltip="Total number of times your brand was viewed by HCP's during the campaign"
              labels={formattedBrandExposureData.labels}
              series={formattedBrandExposureData.series}
            />
          ) : isBrandExposureLoading ? (
            <BarChartSkeleton />
          ) : (
            <CardNotFound
              title={"Brand Exposure"}
              height={360}
              tooltip="Total number of times your brand was viewed by HCP's during the campaign"
            />
          )}
        </Col>
        <Col md={6} sm={12} className="d-flex">
          {!isUniqueUsersLoading && uniqueUsers?.length > 0 ? (
            <MixedColumnChart
              title={"Unique Users"}
              // yaxisFrontTitle="Unique Users (Cumulative)"
              // yaxisBackTitle="Unique Users Per Day"
              tooltip="Total distinct users who engaged with your campaign over the selected period"
              labels={formattedUniqueUsersData.labels}
              series={formattedUniqueUsersData.series}
              needImageDownload={true}
              needCSVDownload={true}
            />
          ) : isUniqueUsersLoading ? (
            <BarChartSkeleton />
          ) : (
            <CardNotFound
              title={"Unique Users"}
              height={360}
              tooltip="Total distinct users who engaged with your campaign over the selected period"
            />
          )}
        </Col>
      </Row>

      <Row>
        <Col sm={12}>
          <Card className="rounded-3">
            <TableHeader
              title="HCP's Details"
              searchProps={{
                isNeeded: true,
                placeholder: "Search via Location / Device / IP Address",
                searchText: searchText,
                setSearchText: setSearchText,
              }}
              downloadProps={{
                needCsvDownload: true,
                exportCSVHandler: exportCSV,
              }}
            />
            <Card.Body>
              {!isUsersListLoading ? (
                <ReactTable
                  data={Array.isArray(filteredUsers) ? filteredUsers : []}
                  columns={LOCATION_REPORT_TABLE_COLUMNS}
                  recordsTotal={totalCount}
                  isPaginationShow={false}
                  currentPageNumber={currentPageNumber}
                  bordered
                  contentCenter
                  noDataText="No Data Available"
                  onTableStateChange={onTableStateChange}
                />
              ) : (
                <CommonTableLoader
                  tableRowLength={5}
                  hasSearch={false}
                  hasActions={false}
                />
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ConsentedLeads;
